import clsx from 'clsx';
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { confirm } from '@/utils/confirm';
import useDeleteAdminImportRequest from '../../api/useDeleteAdminImportRequest';
import useFetchAdminIam from '../../api/useFetchAdminIam';
import useFetchAdminImportRequests from '../../api/useFetchAdminImportRequests';
import AdminStatusBadge from '../../components/atoms/AdminStatusBadge';
import AdminLoadingState from '../../components/molecules/AdminLoadingState';
import AdminErrorState from '../../components/molecules/AdminErrorState';
import { IMPORT_REQUEST_STATUS_STYLES } from '../../constants/status';
import { formatDateTime } from '../../utils/formatters';

const TABLE_HEADERS = [
  { key: 'organization_name', label: 'クライアント', width: '1fr' },
  { key: 'status', label: 'ステータス', width: '120px' },
  { key: 'admin_user_name', label: '対応者', width: '140px' },
  { key: 'created_at', label: '作成日', width: '140px' },
  { key: 'actions', label: '操作', width: '100px' },
] as const;

const gridTemplateColumns = TABLE_HEADERS.map((h) => h.width).join(' ');

const getCellContent = (
  request: {
    organization_name?: string | null;
    status: string;
    admin_user_name?: string | null;
    created_at?: string | null;
  },
  key: (typeof TABLE_HEADERS)[number]['key']
): ReactNode => {
  switch (key) {
    case 'organization_name':
      return request.organization_name ?? '-';
    case 'status':
      return <AdminStatusBadge status={request.status} statusMap={IMPORT_REQUEST_STATUS_STYLES} />;
    case 'admin_user_name':
      return request.admin_user_name ?? '-';
    case 'created_at':
      return formatDateTime(request.created_at);
    default:
      return '-';
  }
};

export default function ImportRequestsPage() {
  const { data: currentUser } = useFetchAdminIam();
  const { data, isLoading, isError } = useFetchAdminImportRequests();
  const { mutate: deleteRequest } = useDeleteAdminImportRequest();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  if (isLoading) return <AdminLoadingState />;
  if (isError || !data)
    return <AdminErrorState message="求人クロールリクエスト一覧の読み込みに失敗しました。" />;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex items-center gap-3">
        <h1 className="my-0 text-2xl leading-[1.2] font-bold text-[#333]">
          求人クロールリクエスト
        </h1>
      </div>
      <div className="min-w-0 overflow-x-auto rounded border border-solid border-[#ccc] bg-white shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)]">
        <div
          className="grid min-w-[400px] border-0 border-b border-solid border-[#ccc] bg-[#F3F7FE]"
          style={{ gridTemplateColumns: gridTemplateColumns }}
        >
          {TABLE_HEADERS.map((header) => (
            <div
              key={header.key}
              className="flex items-center px-4 py-2 text-xs font-bold text-black"
            >
              {header.label}
            </div>
          ))}
        </div>
        {data.length === 0 ? (
          <div className="p-6 text-sm text-sub">求人クロールリクエストはありません</div>
        ) : (
          <div className="flex flex-col">
            {data.map((request, index) => (
              <Link
                key={request.id}
                to="/admin"
                search={{ tab: 'import_requests', importRequestId: request.id }}
                className={clsx(
                  'grid min-w-[400px] border-0 border-b border-solid border-[#ddd] bg-white text-left no-underline hover:bg-gray',
                  index === data.length - 1 && 'border-b-0'
                )}
                style={{ gridTemplateColumns: gridTemplateColumns }}
              >
                {TABLE_HEADERS.map((header) => (
                  <div
                    key={header.key}
                    className={
                      header.key === 'status'
                        ? 'flex items-center px-4 py-3'
                        : 'flex items-center p-4 text-sm text-black'
                    }
                  >
                    {header.key === 'actions' ? (
                      isSuperAdmin ? (
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const confirmed = await confirm({
                              title: 'リクエストを削除',
                              message: 'このリクエストを削除します。よろしいですか？',
                            });
                            if (!confirmed) return;
                            deleteRequest(request.id);
                          }}
                          className="rounded border border-solid border-red-300 bg-white px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                        >
                          削除
                        </button>
                      ) : (
                        '-'
                      )
                    ) : (
                      getCellContent(request, header.key)
                    )}
                  </div>
                ))}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
// admin は検索パラメータでタブ切り替えのため HMR が伝わりにくい。更新を確実に反映するため受け取りで無効化してフルリロードする。
if (import.meta.hot) {
  import.meta.hot.accept((newModule) => {
    if (newModule) import.meta.hot?.invalidate();
  });
}
